import { InfoCircleTwoTone } from '@ant-design/icons';
import {
  Card,
  ColorPicker,
  ConfigProvider,
  Flex,
  Form,
  Select,
  Tag,
  Typography,
} from 'antd';
import { Color } from 'antd/es/color-picker';
import { TFunction, useTranslation } from 'translations/hooks';

import { PlotterMode } from 'business/data-analysis/constants';
import { ChartType } from 'business/data-analysis/pages/graph/components/chart/types';
import {
  useGraphSet,
  useGraphSetDispatch,
} from 'business/data-analysis/pages/graph/hooks/graph-context/contexts';
import { isRingParameter } from 'business/data-analysis/pages/graph/hooks/graph-context/is-ring-parameter';
import {
  GraphSetDispatchActions,
  PeriodParameter,
  RingParameter,
} from 'business/data-analysis/pages/graph/hooks/graph-context/types';
import { useGraphParameter } from 'business/data-analysis/pages/graph/hooks/use-graph-parameter';
import { useMode } from 'business/data-analysis/pages/graph/hooks/use-mode';
import { getColorPresets } from 'technical/color-picker-presets';
import { useMediaType } from 'technical/media/hooks';
import Button from 'ui/button';
import TrashIcon from 'ui/icons/trash';

import styles from './index.module.scss';

interface Props {
  graphId: string;
  parameterUniqueId: string;
}

const computeTypeOptions = (t: TFunction) => [
  {
    value: 'excavation',
    label: t('dataAnalysis.ringMode.computeType.type', {
      context: 'excavation',
    }),
  },
  {
    value: 'build',
    label: t('dataAnalysis.ringMode.computeType.type', { context: 'build' }),
  },
  {
    value: 'excavation_and_build',
    label: t('dataAnalysis.ringMode.computeType.type', {
      context: 'excavation_and_build',
    }),
  },
  {
    value: 'total',
    label: t('dataAnalysis.ringMode.computeType.type', { context: 'total' }),
  },
];

const aggregationTypeOptions = (t: TFunction) => [
  {
    value: 'avg',
    label: t('dataAnalysis.ringMode.aggregationType.type', { context: 'avg' }),
  },
  {
    value: 'max',
    label: t('dataAnalysis.ringMode.aggregationType.type', { context: 'max' }),
  },
  {
    value: 'min',
    label: t('dataAnalysis.ringMode.aggregationType.type', { context: 'min' }),
  },
  {
    value: 'last',
    label: t('dataAnalysis.ringMode.aggregationType.type', { context: 'last' }),
  },
];

const chartTypeOptions = (t: TFunction) => {
  const chartTypes = Object.values(ChartType);

  return chartTypes.map((type) => {
    return {
      value: type,
      label: t('dataAnalysis.chartType.type', { context: type }),
    };
  });
};

const ParameterCard = ({ graphId, parameterUniqueId }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaType();

  const mode = useMode();

  const graphSet = useGraphSet();
  const parameter = useGraphParameter(graphId, parameterUniqueId);

  const dispatch = useGraphSetDispatch();

  const changeColor = (color: Color) => {
    dispatch({
      type: GraphSetDispatchActions.UpdateParameter,
      graphId,
      parameterUniqueId,
      update: { color: color.toHexString() },
    });
  };

  const changeComputeType = (computeType: RingParameter['computeType']) => {
    dispatch({
      type: GraphSetDispatchActions.UpdateParameter,
      graphId,
      parameterUniqueId,
      update: { computeType },
    });
  };

  const changeAggregationType = (
    aggregationType: RingParameter['aggregationType'],
  ) => {
    dispatch({
      type: GraphSetDispatchActions.UpdateParameter,
      graphId,
      parameterUniqueId,
      update: { aggregationType },
    });
  };

  const changeChartType = (chartType: PeriodParameter['chartType']) => {
    dispatch({
      type: GraphSetDispatchActions.UpdateParameter,
      graphId,
      parameterUniqueId,
      update: { chartType },
    });
  };

  const colorPresets = getColorPresets(t('dataAnalysis.ringMode.colorPresets'));

  return (
    <Card className={styles.parameterCard}>
      <Flex vertical gap={16}>
        <Flex justify="space-between" gap={8}>
          <Flex align="center" gap={10}>
            {isMobile ? null : (
              <ColorPicker
                size="small"
                onChangeComplete={changeColor}
                disabledAlpha
                value={parameter.color}
                presets={colorPresets}
              />
            )}
            <Flex vertical justify="space-between" gap={4}>
              <Typography>{parameter.name}</Typography>
              <Tag className={styles.unit}>{parameter.unit}</Tag>
            </Flex>
          </Flex>
          <Button
            className={styles.removeParameter}
            icon={<TrashIcon className={styles.removeParameterIcon} />}
            onClick={() =>
              dispatch({
                type: GraphSetDispatchActions.RemoveParameters,
                graphId,
                parameterUniqueIds: [parameterUniqueId],
              })
            }
          />
        </Flex>
        <Flex vertical gap={8}>
          <ConfigProvider
            // Reset all whitespaces around form item
            theme={{
              components: {
                Form: { itemMarginBottom: 0, labelHeight: 0, controlHeight: 0 },
              },
            }}
          >
            {isRingParameter(graphSet, parameter) && (
              <>
                <Form.Item
                  label={t('dataAnalysis.ringMode.computeType.title')}
                  tooltip={{
                    title: t('dataAnalysis.ringMode.computeType.tooltip'),
                    icon: <InfoCircleTwoTone />,
                  }}
                >
                  <Select
                    size="small"
                    value={parameter.computeType}
                    onChange={changeComputeType}
                    options={computeTypeOptions(t)}
                  />
                </Form.Item>

                <Form.Item
                  label={t('dataAnalysis.ringMode.aggregationType.title')}
                  tooltip={{
                    title: t('dataAnalysis.ringMode.aggregationType.tooltip'),
                    icon: <InfoCircleTwoTone />,
                  }}
                >
                  <Select
                    size="small"
                    value={parameter.aggregationType}
                    onChange={changeAggregationType}
                    options={aggregationTypeOptions(t)}
                  />
                </Form.Item>
              </>
            )}
            {mode === PlotterMode.RING ? (
              <Form.Item
                label={t('dataAnalysis.chartType.title')}
                tooltip={{
                  title: t('dataAnalysis.chartType.tooltip'),
                  icon: <InfoCircleTwoTone />,
                }}
              >
                <Select
                  size="small"
                  value={parameter.chartType}
                  onChange={changeChartType}
                  options={chartTypeOptions(t)}
                />
              </Form.Item>
            ) : null}
          </ConfigProvider>
        </Flex>
      </Flex>
    </Card>
  );
};

export default ParameterCard;
